import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Search, Filter, Calendar, FileText, AlertCircle, TrendingUp, Download, Bell, Info } from 'lucide-react';
import { pngNewsService } from '../../services/pngNewsService';
import { pngHolidaysService } from '../../services/pngHolidaysService';
import NewsCategoryFilter from './components/NewsCategoryFilter';
import NewsCard from './components/NewsCard';
import HolidayCalendar from './components/HolidayCalendar';
import GazettePublicationCard from './components/GazettePublicationCard';
import Icon from '../../components/AppIcon';


const GovernmentNewsUpdatesCenter = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newsData, setNewsData] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [gazettePublications, setGazettePublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedCategory, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch news based on active tab
      if (activeTab === 'all' || activeTab === 'news') {
        const filters = {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          search: searchQuery || undefined
        };
        const news = await pngNewsService?.getAllNews(filters);
        setNewsData(news || []);
      }

      // Fetch holidays
      if (activeTab === 'all' || activeTab === 'holidays') {
        const holidaysData = await pngHolidaysService?.getUpcomingHolidays();
        setHolidays(holidaysData || []);
      }

      // Fetch gazette publications (filter news by gazette category)
      if (activeTab === 'all' || activeTab === 'gazette') {
        const gazetteData = await pngNewsService?.getAllNews({ category: 'gazette' });
        setGazettePublications(gazetteData || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSearch = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const tabs = [
    { id: 'all', label: 'All Updates', icon: TrendingUp },
    { id: 'news', label: 'Latest News', icon: FileText },
    { id: 'holidays', label: 'Public Holidays', icon: Calendar },
    { id: 'gazette', label: 'Gazette Publications', icon: FileText }
  ];

  const featuredNews = newsData?.filter(news => news?.isFeatured)?.slice(0, 3) || [];
  const regularNews = newsData?.filter(news => !news?.isFeatured) || [];

  return (
    <>
      <Helmet>
        <title>Government News & Updates Center - PNG HR Compliance</title>
        <meta name="description" content="Access latest PNG government news, public holidays, and gazette publications relevant to HR operations" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Government News & Updates Center</h1>
            <p className="text-gray-600">Stay informed with official PNG government communications, regulatory updates, and public information</p>
          </div>

          {/* Information Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">About Government Sources</h3>
                <p className="text-sm text-blue-800">
                  News items link to official PNG government websites including:
                  <span className="font-medium"> Department of Labour and Industrial Relations (DLIR)</span>,
                  <span className="font-medium"> Internal Revenue Commission (IRC)</span>, and
                  <span className="font-medium"> NASFUND</span>.
                  For specific documents, gazette publications, or detailed information, please visit the respective department websites or contact them directly.
                </p>
              </div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search news, updates, or gazette publications..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Notification Settings */}
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Bell className="w-5 h-5" />
                <span className="hidden md:inline">Alert Settings</span>
              </button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {tabs?.map((tab) => {
                  const Icon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                        ${activeTab === tab?.id
                          ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      {tab?.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Content Grid */}
          {!loading && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Sidebar - Categories */}
              <div className="lg:col-span-3">
                <NewsCategoryFilter
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                />
              </div>

              {/* Main Content Area */}
              <div className="lg:col-span-9">
                {/* All Updates Tab */}
                {activeTab === 'all' && (
                  <div className="space-y-6">
                    {/* Featured News Section */}
                    {featuredNews?.length > 0 && (
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Featured Updates</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {featuredNews?.map((news) => (
                            <NewsCard key={news?.id} news={news} featured />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recent News */}
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Updates</h2>
                      <div className="space-y-4">
                        {regularNews?.slice(0, 5)?.map((news) => (
                          <NewsCard key={news?.id} news={news} />
                        ))}
                      </div>
                    </div>

                    {/* Upcoming Holidays Preview */}
                    {holidays?.length > 0 && (
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Upcoming Public Holidays</h2>
                        <HolidayCalendar holidays={holidays?.slice(0, 3)} compact />
                      </div>
                    )}

                    {/* Recent Gazette Publications */}
                    {gazettePublications?.length > 0 && (
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Gazette Publications</h2>
                        <div className="space-y-3">
                          {gazettePublications?.slice(0, 3)?.map((gazette) => (
                            <GazettePublicationCard key={gazette?.id} gazette={gazette} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Latest News Tab */}
                {activeTab === 'news' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Latest News</h2>
                      <span className="text-sm text-gray-500">{newsData?.length || 0} articles</span>
                    </div>
                    {newsData?.length > 0 ? (
                      newsData?.map((news) => (
                        <NewsCard key={news?.id} news={news} />
                      ))
                    ) : (
                      <div className="text-center py-12 bg-white rounded-lg">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No news articles found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Public Holidays Tab */}
                {activeTab === 'holidays' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Public Holidays</h2>
                      <button className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                        Export Calendar
                      </button>
                    </div>
                    <HolidayCalendar holidays={holidays} />
                  </div>
                )}

                {/* Gazette Publications Tab */}
                {activeTab === 'gazette' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Gazette Publications</h2>
                      <span className="text-sm text-gray-500">{gazettePublications?.length || 0} publications</span>
                    </div>
                    {gazettePublications?.length > 0 ? (
                      gazettePublications?.map((gazette) => (
                        <GazettePublicationCard key={gazette?.id} gazette={gazette} />
                      ))
                    ) : (
                      <div className="text-center py-12 bg-white rounded-lg">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No gazette publications found</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GovernmentNewsUpdatesCenter;